// « Nouvelle demande » — wizard 4 étapes (port du RecWizard v12):
// entité + rôle → séniorité/contrat/motif/date → objectifs (v2 : 3–5
// résultats à 90 jours, 1–3 à 12 mois) → justification + récap.
// Soumettre = POST /recrutement/requisitions → EN_ATTENTE, chaîne armée.
import { useState } from 'react';
import { ENTITIES } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useUnsavedGuard } from '../../lib/unsaved.js';
import {
  useCreateRecRequisition, useMe, type RecRoleDTO,
} from '../../api/hooks.js';

interface Props {
  roles: RecRoleDTO[];
  onClose: () => void;
}

const SENIORITIES = ['Junior', 'Confirmé', 'Senior'] as const;
const CONTRATS = [
  { lbl: 'CDI', type: 'CDI' as const, months: undefined },
  { lbl: 'CDD 6 mois', type: 'CDD' as const, months: 6 },
  { lbl: 'CDD 12 mois', type: 'CDD' as const, months: 12 },
];
const MOTIFS = ['Création de poste', 'Remplacement', 'Remplacement de…'] as const;

const defaultTarget = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 2);
  return d.toISOString().slice(0, 10);
};

export function RecWizard({ roles, onClose }: Props) {
  const me = useMe();
  const create = useCreateRecRequisition();
  const scope = me.data?.entityScope ?? [];
  const ents = ENTITIES.filter((e) => scope.length === 0 || scope.includes(e.id));

  const [st, setSt] = useState(0);
  const [ent, setEnt] = useState<string | null>(ents.length === 1 ? ents[0]!.id : null);
  const [role, setRole] = useState<string | null>(null);
  const [senior, setSenior] = useState<string>('Confirmé');
  const [contrat, setContrat] = useState(0);
  const [motif, setMotif] = useState<string>(MOTIFS[0]);
  const [qui, setQui] = useState('');
  const [target, setTarget] = useState(defaultTarget());
  const [justif, setJustif] = useState('');
  const [o90, setO90] = useState<string[]>(['', '', '']);
  const [o12, setO12] = useState<string[]>(['']);
  const f90 = o90.map((x) => x.trim()).filter(Boolean);
  const f12 = o12.map((x) => x.trim()).filter(Boolean);
  const okObj = f90.length >= 3 && f90.length <= 5 && f12.length >= 1 && f12.length <= 3;

  const selRole = roles.find((r) => r.id === role);
  const selEnt = ents.find((e) => e.id === ent);
  const ok0 = !!ent && !!role;
  const ok2 = justif.trim().length > 10;
  useUnsavedGuard({
    id: 'rec-wizard',
    dirty: st > 0 || !!role || justif.trim() !== '',
    label: 'Recrutement · nouveau besoin',
    summary: selRole?.label,
    discard: onClose,
  });

  const submit = () => {
    const c = CONTRATS[contrat]!;
    create.mutate(
      {
        roleId: role!, entityId: ent!, seniority: senior,
        contractType: c.type, ...(c.months ? { cddMonths: c.months } : {}),
        motif, ...(motif === 'Remplacement de…' ? { replacedName: qui } : {}),
        targetStart: target, justification: justif.trim(),
        outcomes90: f90, outcomes12m: f12,
      },
      {
        onSuccess: () => {
          onClose();
          toast.success('Demande soumise — validation directeur → owner. Rien ne se publie avant validation.');
        },
        onError: (err) => {
          const body = (err as { body?: { message?: string } }).body;
          toast.danger(body?.message ?? 'Échec de la soumission de la demande.');
        },
      },
    );
  };

  return (
    <div className="rec-modal-ov" onClick={onClose}>
      <div className="rec-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-h">
          <h3>Nouvelle demande de recrutement</h3>
          <button className="rec-x" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <div className="rec-modal-b">
          <div className="rec-steps">{[0, 1, 2, 3].map((i) => <i key={i} className={st >= i ? 'on' : ''}></i>)}</div>

          {st === 0 && (
            <div>
              <div className="rec-lbl">Entité{scope.length > 0 && <span className="rec-lbl-note"> — votre périmètre est limité</span>}</div>
              <div className="rec-entrow">
                {ents.map((e) => (
                  <button key={e.id} className={`rec-epill${ent === e.id ? ' on' : ''}`} onClick={() => setEnt(e.id)}>
                    <span className="d" style={{ background: e.color }}></span>{e.name}
                  </button>
                ))}
              </div>
              <div className="rec-lbl">Rôle — depuis le catalogue (templates hérités)</div>
              <div className="rec-pick">
                {roles.map((r) => {
                  const ready = r.tplReady.filter((t) => t.ready).length;
                  return (
                    <button key={r.id} className={`rec-pickbtn${role === r.id ? ' on' : ''}`} onClick={() => setRole(r.id)}>
                      <b>{r.label}</b>
                      <span>{r.dept} · {ready}/{r.tplReady.length} templates prêts</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {st === 1 && (
            <div>
              <div className="rec-lbl">Séniorité</div>
              <div className="rec-seg">
                {SENIORITIES.map((x) => <button key={x} className={senior === x ? 'on' : ''} onClick={() => setSenior(x)}>{x}</button>)}
              </div>
              <div className="rec-lbl">Contrat</div>
              <div className="rec-seg">
                {CONTRATS.map((c, i) => <button key={c.lbl} className={contrat === i ? 'on' : ''} onClick={() => setContrat(i)}>{c.lbl}</button>)}
              </div>
              <div className="rec-f2">
                <div className="rec-field">
                  <label>Motif</label>
                  <select value={motif} onChange={(e) => setMotif(e.target.value)}>
                    {MOTIFS.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div className="rec-field">
                  <label>Date de démarrage cible</label>
                  <input type="date" value={target} onChange={(e) => setTarget(e.target.value)} />
                </div>
              </div>
              {motif === 'Remplacement de…' && (
                <div className="rec-field">
                  <label>Qui remplace-t-on ?</label>
                  <input value={qui} onChange={(e) => setQui(e.target.value)} placeholder="Nom de la personne" />
                </div>
              )}
            </div>
          )}

          {st === 2 && (
            <div>
              <div className="rec-rule-p">
                <b>Ce que la recrue doit avoir réussi.</b> Montré aux validateurs, annexé à l’offre, suivi à chaque point de la période d’essai.
                Un résultat se constate (« livre seul·e 4 projets par mois »), ce n’est pas une qualité (« autonome »).
              </div>
              <div className="rec-lbl">Résultats à 90 jours <span className="rec-lbl-note" style={{ color: f90.length >= 3 ? 'var(--greenc)' : 'var(--warnc)' }}>· {f90.length}/5 — de 3 à 5</span></div>
              <OutcomeRows list={o90} setList={setO90} max={5} ph="Ex. Livrer seul·e 4 projets clients par mois" />
              <div className="rec-lbl">Résultats à 12 mois <span className="rec-lbl-note" style={{ color: f12.length >= 1 ? 'var(--greenc)' : 'var(--warnc)' }}>· {f12.length}/3 — de 1 à 3</span></div>
              <OutcomeRows list={o12} setList={setO12} max={3} ph="Ex. Devenir la référence motion de l’agence" />
            </div>
          )}

          {st === 3 && (
            <div>
              <div className="rec-field">
                <label>Justification — lue par le directeur puis l'owner</label>
                <textarea rows={3} value={justif} onChange={(e) => setJustif(e.target.value)}
                  placeholder="Charge, contexte, coût de ne pas recruter…"></textarea>
              </div>
              <div className="rec-recap">
                <div className="r"><span>Poste</span><b>{selRole?.label} · {senior}</b></div>
                <div className="r"><span>Entité</span><b>{selEnt?.name} · {CONTRATS[contrat]!.lbl}</b></div>
                <div className="r"><span>Motif</span><b>{motif}{qui ? ` ${qui}` : ''}</b></div>
                <div className="r"><span>Démarrage cible</span><b>{target.split('-').reverse().join('/')}</b></div>
                <div className="r"><span>Résultats à 90 jours</span><b>{f90.length}</b></div>
                <div className="r"><span>Circuit de validation</span><b>Directeur de département → Owner</b></div>
              </div>
            </div>
          )}

          <div className="rec-modal-f">
            <button className="rec-ghost" onClick={() => (st ? setSt(st - 1) : onClose())}>{st ? '← Retour' : 'Annuler'}</button>
            {st < 3
              ? <button className="rec-go" disabled={(st === 0 && !ok0) || (st === 2 && !okObj)} onClick={() => setSt(st + 1)}>Continuer →</button>
              : <button className="rec-go" disabled={!ok2 || create.isPending} onClick={submit}>Soumettre à validation</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function OutcomeRows({ list, setList, max, ph }: { list: string[]; setList: (l: string[]) => void; max: number; ph: string }) {
  return (
    <div>
      {list.map((v, i) => (
        <div key={i} className="rec-outrow">
          <span className="rec-vchip">{i + 1}</span>
          <input value={v} placeholder={ph} onChange={(e) => { const a = [...list]; a[i] = e.target.value; setList(a); }} />
          <button className="rec-x sm" onClick={() => setList(list.filter((_, j) => j !== i))} aria-label="Supprimer">✕</button>
        </div>
      ))}
      {list.length < max && <button className="rec-addrow" onClick={() => setList([...list, ''])}>＋ Ajouter un résultat</button>}
    </div>
  );
}
